import type { MetaFunction } from "@remix-run/react"
import {
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  articleSection,
  bold,
  faqGrid,
  faqH3Style,
  figCaptionStyle,
  flexWrap,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  highlightHead,
  imagePreview,
  infoWrap,
  niceTextEffect,
  orderedList,
  subSection,
  testimonialStyle,
  tocItemStyle,
  unorderedList,
} from "./article.css"
import Footer from "~/components/Footer"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import { btnWrap, masonryItem, masonryWrap, twoColFixed } from "./index.css"
import LinkButton from "~/components/LinkButton"
import ScrollyWolly from "~/components/ScrollyWolly"
import Link from "~/components/Link"
import StickyHeader from "~/components/StickyHeader"
import { buttonStyle } from "~/components/StickyHeader/header.css"
import { golden, vars } from "~/components/styles/theme.css"
import { Bold, H2, H3, P, Section } from "~/components/articleComponents"

const masonryImgUrls = [
  {
    url: "/landing/grid/2fd1277d-00c4-460a-ab30-a9901cf3e1251.webp",
    alt: "AI Landscape Design generates a tropical oasis flower patch, welcoming visitors and creating a tranquil atmosphere.",
  },
  {
    url: "/landing/grid/5cffdd1c-7ee5-4667-b39a-cb3681fad70f3.webp",
    alt: "A modern yet wild flowing flower bed creates a wonderful landscape environment.",
  },
  {
    url: "/landing/grid/cozy-balcony-ai-generated.webp",
    alt: "AI Landscape Design has generated this cozy balcony garden improvement. With some greenery and thoughtful placement, the balcony has been transformed.",
  },
  {
    url: "/landing/grid/c20b1846-d4bd-442b-abf6-aa9f1074b9342.webp",
    alt: "A calm serene AI  garden landscape design. Incorporating a rocking chair and natural moss ground, this patio has been reimagined with peace and beauty in mind.",
  },
  {
    url: "/landing/grid/f2d0575e-8e19-4002-8109-970f4b561c3e2.webp",
    alt: "A grand pergola covered in climbing plants, inspiring creativity for unique landscaped spaces.",
  },
]

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

// SEO FIX: Title shortened from 71 to 54 chars (was over 60 char limit)
const TITLE = "AI Landscape Design | Professional Garden Concepts"
// SEO FIX: Description shortened from 173 to 148 chars (was over 155 char limit)
const DESCRIPTION =
  "Upload a photo and generate professional AI landscape design ideas in seconds. Explore styles, materials and layouts for your outdoor space. Try Rescape AI."
const IMAGE = "https://rescapeai.com/guide/garden-5-after-ai-contemporary-design.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/ai-landscape-design",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-07-06",
        dateModified: "2024-07-06",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/ai-landscape-design",
        },
        articleSection: [
          "Landscaping",
          "Landscape Design",
          "Artificial Intelligence",
          "Technology",
        ],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/ai-yard-design-patio-and-flowerbeds.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            AI Landscape Design: Generate Ideas from Your Own Garden
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <Section>
          <P>
            Landscape design can feel intimidating — levels, paths, materials, planting
            "masses," focal points… there's a lot to consider. AI landscape design tools
            simplify the early stages by giving you fast, visual concepts to guide your
            thinking.
          </P>
          <P>
            Whether you're a homeowner planning a remodel or a gardener looking for design
            inspiration, AI-generated images help you explore styles and structures with
            clarity.
          </P>
          <ScrollyWolly
            before={{
              image: "/articles/before-ai-yard-design-patio.webp",
              title: "Your Backyard",
              alt: "A functional backyard with zero ambition",
            }}
            after={{
              image: "/articles/ai-yard-design-patio-and-flowerbeds.webp",
              title: "AI generated",
              alt: "Our AI landscape design realises the customers vision with beautiful paving opportunities and even recommends a better set of chairs...",
            }}
            height="478px"
          />
        </Section>
        <Section>
          <H2>What AI Landscape Design Offers</H2>
          <P>Unlike garden-only tools, AI landscape design inspiration focuses on:</P>
          <ul className={unorderedList}>
            <li>structure and layout</li>
            <li>materials and hardscaping</li>
            <li>levels, steps, and terraces</li>
            <li>focal points and symmetry</li>
          </ul>
          <P>
            It's ideal for anyone wanting a garden that looks "designed," not just
            planted.
          </P>
        </Section>
        <Section>
          <H2>Explore Landscaping Styles Through AI</H2>
          <P>Try generating concepts for different design styles:</P>
          <ul className={unorderedList}>
            <li>Modern geometric landscaping</li>
            <li>Mediterranean terraces</li>
            <li>Minimalist Japanese-inspired layouts</li>
            <li>Traditional English-style landscaping</li>
            <li>Drought-tolerant, gravel-heavy designs</li>
            <li>Textured gardens with grasses and shrubs</li>
          </ul>
        </Section>
        <Section>
          <div className={masonryWrap}>
            {masonryImgUrls.map(({ url, alt }) => (
              <img
                key={url}
                src={url}
                alt={alt}
                className={masonryItem}
                style={{ width: "100%", height: "auto", borderRadius: "6px" }}
                loading="lazy"
              />
            ))}
          </div>
        </Section>
        <Section>
          <H2>See Hardscape + Plants Working Together</H2>
          <P>
            Landscape design is about the{" "}
            <Bold>balance between structure and planting.</Bold>
          </P>
          <P>AI helps you imagine:</P>
          <ul className={unorderedList}>
            <li>stone pathways</li>
            <li>retaining walls</li>
            <li>timber or steel edging</li>
            <li>raised beds</li>
            <li>seating zones</li>
            <li>water features</li>
            <li>hedges, shrubs, and mass planting</li>
          </ul>
          <P>You get a sense of the "flow" and overall aesthetic.</P>
          <ScrollyWolly
            before={{
              image: "/ai-landscape-design/original.jpg",
              title: "The garden before",
              alt: "The garden after moving in",
            }}
            after={{
              image: "/ai-landscape-design/zen.webp",
              title: "Zen garden inspiration",
              alt: "Teeming with plant life, the AI landscape design presents clear and inspired options, featuring a thoughtfully executed color palette that enhances the overall aesthetic.",
            }}
            height="380px"
          />
        </Section>
        <Section>
          <H3>Using AI for Concept Boards & Client Conversations</H3>
          <P>For professional gardeners or landscapers, AI is powerful for:</P>
          <ul className={unorderedList}>
            <li>early-stage client moodboards</li>
            <li>style exploration</li>
            <li>presenting multiple options quickly</li>
            <li>visualising material palettes</li>
          </ul>
          <P>
            It helps clients picture the end result long before detailed plans are drawn.
          </P>
          <div className={infoWrap}>
            <figure>
              <div className={articleCompareImages}>
                <img
                  src={"/guide/garden-5-before-ai-design.webp"}
                  alt={"Garden before AI landscape design"}
                  loading="lazy"
                />
                <img
                  src={"/guide/garden-5-after-ai-contemporary-design.webp"}
                  alt={"Contemporary landscaping design with decking"}
                  loading="lazy"
                />
              </div>
              <img
                className={articleImageStyle}
                src={"/guide/garden-5-after-ai-contemporary-design.webp"}
                alt={"Contemporary landscaping design with decking after AI redesign"}
                loading="lazy"
                style={{
                  height: "320px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "0% 80%",
                  marginTop: "2rem",
                }}
              />
              <figcaption className={figCaptionStyle}>
                Rescape AI has invisioned this landscaping project with a large scale
                transformation including a large decking space
              </figcaption>
            </figure>
          </div>
        </Section>
        <Section>
          <H2>Limitations to Keep in Mind</H2>
          <P>AI-generated landscape concepts:</P>
          <ul className={unorderedList}>
            <li>don't include real measurements</li>
            <li>don't create build-ready plans</li>
            <li>don't account for drainage, soil, or utilities</li>
          </ul>
          <P>
            Think of them as <Bold>visual brainstorming tools</Bold> — they help with
            direction, but professionals handle the details.
          </P>
        </Section>
        <Section>
          <H3>Final Thoughts</H3>
          <P>
            AI landscape design tools make it easier than ever to explore ideas, test
            materials, and visualise structured outdoor designs. Whether you're updating
            part of your garden or planning a full renovation, these visuals give you the
            inspiration you need to move forward with confidence.
          </P>
        </Section>
        <Section>
          <H2>FAQ's</H2>

          <div className={faqGrid}>
            <div>
              <h3 className={faqH3Style}>
                How accurate are AI landscape design concepts when planning a real garden?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                AI landscape design tools are great for generating conceptual outdoor
                layouts, but they don't provide exact measurements or account for
                drainage, soil type, or utilities. Think of them as visual moodboards that
                help you explore structure, styles, and materials. For build-ready plans,
                you'll still need a professional designer or landscaper.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                Can AI landscape design help me choose the right materials for my outdoor
                space?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Yes. Many tools offer AI hardscape design suggestions, helping you
                visualise stone paths, decks, retaining walls, or seating areas alongside
                plants. This makes it easier to compare materials and understand how
                they'll interact with the overall layout before making decisions.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                Is AI landscape design useful for professional landscapers and garden
                designers?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Definitely. Professionals often use AI garden visualization to create
                early-stage concept boards, present multiple styles quickly, and help
                clients understand potential layouts. It speeds up communication and lets
                clients react to visual ideas long before detailed plans are drafted.
              </p>
            </div>
          </div>
        </Section>
        <Footer />
      </div>
    </>
  )
}

export default Index
